/**
 * Anonymous ID Utility
 * 
 * Generates, stores, and retrieves anonymous user IDs for users who haven't authenticated.
 * Uses localStorage for persistence across browser sessions.
 */

const ANON_ID_KEY = 'genanything_anon_id';

/**
 * Generate a new anonymous ID (UUID v4)
 */
function generateAnonId(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create an anonymous ID
 * If an ID doesn't exist, generates a new one and stores it in localStorage
 * 
 * @returns The anonymous ID (UUID string)
 */
export function getOrCreateAnonId(): string {
  try {
    // Try to get existing anon_id from localStorage
    const existingId = localStorage.getItem(ANON_ID_KEY);
    
    if (existingId && existingId.trim() !== '') {
      return existingId;
    }
    
    // Generate new anon_id
    const newId = generateAnonId();
    
    // Store in localStorage
    localStorage.setItem(ANON_ID_KEY, newId);
    
    return newId;
  } catch (error) {
    // If localStorage is not available (e.g., private browsing), generate a temporary ID
    // This won't persist, but at least the app won't crash
    console.warn('localStorage not available, using temporary anon_id:', error);
    return generateAnonId();
  }
}

/**
 * Get the current anonymous ID without creating a new one
 * 
 * @returns The anonymous ID if it exists, null otherwise
 */
export function getAnonId(): string | null {
  try {
    const id = localStorage.getItem(ANON_ID_KEY);
    return id && id.trim() !== '' ? id : null;
  } catch (error) {
    console.warn('Failed to get anon_id from localStorage:', error);
    return null;
  }
}

/**
 * Clear the anonymous ID from localStorage
 * Useful when a user authenticates and we want to switch to their real user ID
 */
export function clearAnonId(): void {
  try {
    localStorage.removeItem(ANON_ID_KEY);
  } catch (error) {
    console.warn('Failed to clear anon_id from localStorage:', error);
  }
}

/**
 * Check if an anonymous ID exists
 * 
 * @returns true if an anon_id exists, false otherwise
 */
export function hasAnonId(): boolean {
  return getAnonId() !== null;
}



